import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Checkbox, Dialog, FormControlLabel, TextField } from "@mui/material";

import DialogActions from "../../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../../containers/DialogTitle/DialogTitle";

import { DialogContent } from "./styles";
import { NewTransactionType, Props, State } from "./interfaces";

import ConfirmationChangesDialog from "../../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import LoadingOverlay from "../../../../../containers/LoadingOverlay/LoadingOverlay";

import isEqual from "lodash/isEqual";

import { BANK_TYPES } from "../../../../../utils/constants/constants";

import { CashTransactionDTO, CashTransactionSTO } from "../../../../../models/cash-transaction";
import { BankTransactionDTO, BankTransactionSTO } from "../../../../../models/bank-transaction";
import { convertDateToUTC, isValidDate } from "../../../../../utils/time-utils";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { saveBankTransaction } from "../../../../../services/bank-transaction/bank-transaction-api.service";
import { saveCashTransaction } from "../../../../../services/cash-transaction/cash-transaction-api.service";
import AddEditButton from "../../../../../reusable-components/AddEditButton/AddEditButton";
import { TextFieldTypeAmount } from "../../../../../reusable-components/Table/AddEditItem/TextFieldTypeAmount/TextFieldTypeAmount";

export const AddBankCashTransaction = ({
  currItem,
  handleNotifyParent,
  buttonVariant,
  noBankAndCashAccounts,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_new_bank_or_cash_transaction";

  const DEFAULT_BANK_CASH_TRANSACTIONS: NewTransactionType = useMemo(() => {
    return {
      cashTransaction: {
        id: "",
        date: new Date(),
        concept: "",
        amount: 0,
        companyAccount: currItem?.companyAccount ?? null,
        legalEntity: currItem?.legalEntity ?? null,
        cashStatement: currItem ?? null,
        balance: 0,
        justificationDoc: null,
        transactions: [],
        isForecast: false,
        isDeleted: false,
      },
      bankTransaction: {
        id: "",
        date: new Date(),
        concept: "",
        amount: 0,
        companyAccount: currItem?.companyAccount ?? null,
        legalEntity: currItem?.legalEntity ?? null,
        bankAccount: currItem ?? null,
        balance: 0,
        justificationDoc: null,
        transactions: [],
        isForecast: false,
        isDeleted: false,
      },
    };
  }, [currItem]);

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      bankAndCashTransactions: DEFAULT_BANK_CASH_TRANSACTIONS as NewTransactionType,
      isCCDOpen: false,
      areFieldsValidated: true,
    };
  }, [DEFAULT_BANK_CASH_TRANSACTIONS, currItem]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleChangeBankAndCashField = (
    field: keyof CashTransactionDTO | keyof BankTransactionDTO,
    value: CashTransactionDTO[keyof CashTransactionDTO] | BankTransactionDTO[keyof BankTransactionDTO]
  ) => {
    setState((state) => ({
      ...state,
      bankAndCashTransactions: {
        ...state.bankAndCashTransactions,
        cashTransaction: {
          ...state.bankAndCashTransactions.cashTransaction,
          [field]: value,
        },
        bankTransaction: {
          ...state.bankAndCashTransactions.bankTransaction,
          [field]: value,
        },
      },
    }));
  };

  const handleChangeText = ({ target: { value, name } }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleChangeBankAndCashField(name as "concept" | "amount", value);
  };

  const handleSave = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));

    try {
      const response =
        currItem?.bankType?.id === BANK_TYPES.bank_accounts.id
          ? await saveBankTransaction({
              ...state.bankAndCashTransactions.bankTransaction,
              id: uuidv4(),
              date: state.bankAndCashTransactions.bankTransaction.date
                ? convertDateToUTC(state.bankAndCashTransactions.bankTransaction.date)
                : null,
            } as BankTransactionSTO)
          : await saveCashTransaction({
              ...state.bankAndCashTransactions.cashTransaction,
              id: uuidv4(),
              date: state.bankAndCashTransactions.cashTransaction.date
                ? convertDateToUTC(state.bankAndCashTransactions.cashTransaction.date)
                : null,
            } as CashTransactionSTO);

      if (!response.error) {
        setState({ ...INITIAL_STATE });

        handleNotifyParent();
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        loading: false,
      }));
    }
  }, [INITIAL_STATE, handleNotifyParent, state.bankAndCashTransactions]);

  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);

  const resetAndCloseDialog = (): void => {
    if (!isEqual(state.bankAndCashTransactions, INITIAL_STATE.bankAndCashTransactions)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };

  const areFieldsValidated = useCallback((): boolean => {
    return (
      !!state.bankAndCashTransactions.bankTransaction.concept &&
      !!isValidDate(state.bankAndCashTransactions.bankTransaction.date)
    );
  }, [state.bankAndCashTransactions]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.bankAndCashTransactions, areFieldsValidated]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      ...INITIAL_STATE,
    }));
  }, [INITIAL_STATE]);

  return (
    <>
      {noBankAndCashAccounts && (
        <>
          <AddEditButton
            title={"Add bank / cash transaction"}
            effect={"add"}
            onClick={() => {
              setState((state) => ({ ...state, isDialogOpen: true }));
            }}
          />
          <Dialog
            open={state.isDialogOpen}
            onClose={resetAndCloseDialog}
            fullWidth
          >
            <LoadingOverlay loading={state.loading}>
              <DialogTitle
                title={t(`${tRP}.title`)}
                handleClose={resetAndCloseDialog}
              />

              <DialogContent>
                {currItem?.bankType?.id === BANK_TYPES.bank_accounts.id ? (
                  <>
                    <DesktopDatePicker
                      value={state.bankAndCashTransactions.bankTransaction.date}
                      onChange={(newValue: Date | null) => {
                        handleChangeBankAndCashField("date", newValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`${t(`${tRP}.fields.date`)}`}
                          required={true}
                        />
                      )}
                    />
                    <TextField
                      label={`${t(`${tRP}.fields.concept`)}`}
                      name="concept"
                      value={state.bankAndCashTransactions.bankTransaction.concept}
                      onChange={handleChangeText}
                      required
                    />
                    <TextFieldTypeAmount
                      field={"amount"}
                      labelKey={`${t(`${tRP}.fields.amount`)}`}
                      amountValue={state.bankAndCashTransactions.bankTransaction.amount ?? 0}
                      handleChange={(_, number) => handleChangeBankAndCashField("amount", number)}
                      required
                    />
                    {/* <TextField
                      label={`${t(`${tRP}.fields.amount`)}`}
                      type="number"
                      value={state.bankAndCashTransactions.bankTransaction.amount}
                      onChange={(e) => handleChangeBankAndCashField("amount", e.target.value)}
                      InputProps={{
                        inputProps: { step: 0.01 },
                      }}
                      onBlur={(e) => handleChangeBankAndCashField("amount", Number(e.target.value).toFixed(2))}
                      required
                    /> */}
                    <FormControlLabel
                      key={"isForecast"}
                      control={
                        <Checkbox
                          checked={state.bankAndCashTransactions.bankTransaction.isForecast}
                          onChange={(_, checked: boolean) => handleChangeBankAndCashField("isForecast", checked)}
                        />
                      }
                      label={`${t(`${tRP}.fields.isForecast`)}`}
                      labelPlacement="end"
                    />
                  </>
                ) : (
                  <>
                    <DesktopDatePicker
                      value={state.bankAndCashTransactions.cashTransaction.date}
                      onChange={(newValue: Date | null) => {
                        handleChangeBankAndCashField("date", newValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={`${t(`${tRP}.fields.date`)}`}
                          required={true}
                        />
                      )}
                    />
                    <TextField
                      label={`${t(`${tRP}.fields.concept`)}`}
                      name="concept"
                      value={state.bankAndCashTransactions.cashTransaction.concept}
                      onChange={handleChangeText}
                    />
                    <TextFieldTypeAmount
                      field={"amount"}
                      labelKey={`${t(`${tRP}.fields.amount`)}`}
                      amountValue={state.bankAndCashTransactions.cashTransaction.amount ?? 0}
                      handleChange={(_, number) => handleChangeBankAndCashField("amount", number)}
                      required
                    />
                    {/* <TextField
                      label={`${t(`${tRP}.fields.amount`)}`}
                      type="number"
                      value={state.bankAndCashTransactions.cashTransaction.amount}
                      onChange={(e) => handleChangeBankAndCashField("amount", e.target.value)}
                      InputProps={{
                        inputProps: { step: 0.01 },
                      }}
                      onBlur={(e) => handleChangeBankAndCashField("amount", Number(e.target.value).toFixed(2))}
                    /> */}
                    <FormControlLabel
                      key={"isForecast"}
                      control={
                        <Checkbox
                          checked={state.bankAndCashTransactions.cashTransaction.isForecast}
                          onChange={(_, checked: boolean) => handleChangeBankAndCashField("isForecast", checked)}
                        />
                      }
                      label={`${t(`${tRP}.fields.isForecast`)}`}
                      labelPlacement="end"
                    />
                  </>
                )}
              </DialogContent>

              <DialogActions
                handleSave={handleSave}
                disabledSaveButton={!areFieldsValidated()}
                handleCancel={resetAndCloseDialog}
                loading={state.loading}
              />
            </LoadingOverlay>
          </Dialog>

          <ConfirmationChangesDialog
            open={state.isCCDOpen}
            handleCancel={() => setState({ ...state, isCCDOpen: false })}
            handleDiscard={handleDiscardChanges}
          />
        </>
      )}
    </>
  );
};
